import { useEffect, useRef, useState } from 'react'
import {
  EInputTypes,
  ETypeOfNotification,
  InputColumnTableProps,
  InputSwitch,
  InputTable,
  InputTextCustom,
  Modal,
  setNotification,
} from 'medere-ui-framework'
import { Button } from 'primereact/button'
import { Dropdown } from 'primereact/dropdown'
import { useDispatch } from 'react-redux'
import LocationInlineEditActions from '../Location/components/LocationInlineEditActions'
import useResizeListener from '../../hooks/useResizeListener'
import LocationMobileActionMenu, { LocationMobileActionItem } from '../Location/tabs/components/LocationMobileActionMenu'
import {
  createBiomedicalEquipmentConfig,
  deleteBiomedicalEquipmentConfig,
  getBiomedicalEquipmentConfigs,
  updateBiomedicalEquipmentConfig,
} from '../../services/BiomedicalEquipmentConfigController'
import {
  BiomedicalEquipmentConfigItemDTO,
  BiomedicalEquipmentConfigSavePayloadDTO,
  BiomedicalEquipmentConnectionType,
} from '../../dto/biomedicalEquipmentConfig/BiomedicalEquipmentConfigDTO'
import './BiomedicalEquipmentConfigView.scss'

interface FormState {
  biomedicalEquipmentConfig: number
  name: string
  aeTitle: string
  url: string
  port: string
  providerSiteCode: string
  user: string
  password: string
  active: boolean
  connectionType: BiomedicalEquipmentConnectionType
}

interface FormErrors {
  name?: string
  aeTitle?: string
  url?: string
  port?: string
  providerSiteCode?: string
  user?: string
  password?: string
}

const connectionTypeOptions: { label: string; value: BiomedicalEquipmentConnectionType }[] = [
  { label: 'Interno', value: BiomedicalEquipmentConnectionType.INTERNAL },
  { label: 'Externo', value: BiomedicalEquipmentConnectionType.EXTERNAL },
]

function connectionTypeLabel(value: BiomedicalEquipmentConnectionType | null | undefined): string {
  return value === BiomedicalEquipmentConnectionType.EXTERNAL ? 'Externo' : 'Interno'
}

const defaultFormState: FormState = {
  biomedicalEquipmentConfig: -1,
  name: '',
  aeTitle: '',
  url: '',
  port: '',
  providerSiteCode: '',
  user: '',
  password: '',
  active: false,
  connectionType: BiomedicalEquipmentConnectionType.INTERNAL,
}

export default function BiomedicalEquipmentConfigView() {
  const dispatch = useDispatch()
  const isMobile = useResizeListener(768)
  const inputTableTopRef = useRef<HTMLDivElement | null>(null)
  const [focusNameInputToken, setFocusNameInputToken] = useState(0)
  const [loading, setLoading] = useState(true)
  const [items, setItems] = useState<BiomedicalEquipmentConfigItemDTO[]>([])
  const [formState, setFormState] = useState<FormState>(defaultFormState)
  const [formErrors, setFormErrors] = useState<FormErrors>({})
  const [editIndex, setEditIndex] = useState<number | null>(null)
  const [itemToDelete, setItemToDelete] = useState<BiomedicalEquipmentConfigItemDTO | null>(null)
  const [deleteModalVisible, setDeleteModalVisible] = useState(false)
  const [mobileModalVisible, setMobileModalVisible] = useState(false)
  const [activeConfirmVisible, setActiveConfirmVisible] = useState(false)
  const [pendingSaveContext, setPendingSaveContext] = useState<'inline' | 'mobile' | null>(null)
  const isEditing = editIndex !== null

  async function loadData() {
    setLoading(true)
    try {
      const configs = await getBiomedicalEquipmentConfigs()
      setItems(configs)
    } catch (error) {
      console.error(error)
      dispatch(setNotification({
        title: 'Error',
        desc: 'No se pudieron cargar las configuraciones de equipos biomédicos.',
        icon: 'pi pi-times',
        typeOfNotification: ETypeOfNotification.ERROR,
      }))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!isEditing || focusNameInputToken === 0) return

    const timer = window.setTimeout(() => {
      const nameInput = inputTableTopRef.current?.querySelector<HTMLInputElement>('.biomedical-equipment-config-name-input input')
      if (!nameInput) return

      nameInput.focus()
      const endPosition = nameInput.value?.length ?? 0
      nameInput.setSelectionRange(endPosition, endPosition)
    }, 0)

    return () => window.clearTimeout(timer)
  }, [focusNameInputToken, isEditing])

  function resetForm() {
    setEditIndex(null)
    setFormErrors({})
    setFormState({
      ...defaultFormState,
    })
  }

  function isValidUrl(value: string): boolean {
    const pattern = /^(https?:\/\/)?([a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*(:\d{1,5})?(\/[^\s]*)?$/
    return pattern.test(value)
  }

  function isValidPort(value: string): boolean {
    if (!/^\d+$/.test(value)) return false
    const n = Number(value)
    return n >= 0 && n <= 65535
  }

  function validateForm(): FormErrors {
    const nextErrors: FormErrors = {}
    if (formState.name.trim() === '') {
      nextErrors.name = 'El nombre es obligatorio.'
    }
    if (formState.url.trim() !== '' && !isValidUrl(formState.url.trim())) {
      nextErrors.url = 'La URL ingresada no es válida.'
    }
    if (formState.connectionType === BiomedicalEquipmentConnectionType.EXTERNAL) {
      if (formState.providerSiteCode.trim() === '') {
        nextErrors.providerSiteCode = 'El código de sitio del proveedor es obligatorio.'
      }
    } else if (formState.port.trim() !== '' && !isValidPort(formState.port.trim())) {
      nextErrors.port = 'El puerto ingresado no es válido.'
    }
    return nextErrors
  }

  function handleConnectionTypeChange(nextType: BiomedicalEquipmentConnectionType) {
    setFormState((current) => ({
      ...current,
      connectionType: nextType,
      port: nextType === BiomedicalEquipmentConnectionType.EXTERNAL ? '' : current.port,
      providerSiteCode: nextType === BiomedicalEquipmentConnectionType.INTERNAL ? '' : current.providerSiteCode,
    }))
    setFormErrors((current) => ({ ...current, port: undefined, providerSiteCode: undefined }))
  }

  function shouldConfirmActivation(): boolean {
    if (!formState.active) return false
    return items.some(
      (item) => item.active && item.biomedicalEquipmentConfig !== formState.biomedicalEquipmentConfig,
    )
  }

  async function deactivateOtherConfigs() {
    const otherActiveItems = items.filter(
      (item) => item.active && item.biomedicalEquipmentConfig !== formState.biomedicalEquipmentConfig,
    )

    await Promise.all(
      otherActiveItems.map((item) =>
        updateBiomedicalEquipmentConfig(item.biomedicalEquipmentConfig, {
          biomedicalEquipmentConfig: item.biomedicalEquipmentConfig,
          name: item.name,
          aeTitle: item.aeTitle,
          url: item.url,
          port: item.port,
          providerSiteCode: item.providerSiteCode,
          user: item.user,
          password: item.password,
          active: false,
          connectionType: item.connectionType,
        }),
      ),
    )
  }

  async function submitSave() {
    const isExternal = formState.connectionType === BiomedicalEquipmentConnectionType.EXTERNAL
    const payload: BiomedicalEquipmentConfigSavePayloadDTO = {
      biomedicalEquipmentConfig: isEditing ? formState.biomedicalEquipmentConfig : null,
      name: formState.name.trim(),
      aeTitle: formState.aeTitle.trim(),
      url: formState.url.trim(),
      port: isExternal ? 0 : (formState.port.trim() === '' ? 0 : Number(formState.port.trim())),
      providerSiteCode: isExternal ? formState.providerSiteCode.trim() : undefined,
      user: formState.user.trim(),
      password: formState.password,
      active: formState.active,
      connectionType: formState.connectionType,
    }

    try {
      if (isEditing) {
        await updateBiomedicalEquipmentConfig(formState.biomedicalEquipmentConfig, payload)
        dispatch(setNotification({
          title: 'Cambios guardados correctamente',
          icon: 'pi pi-check',
          typeOfNotification: ETypeOfNotification.INFO,
        }))
      } else {
        await createBiomedicalEquipmentConfig(payload)
        dispatch(setNotification({
          title: 'Creación exitosa',
          desc: 'La configuración de equipo biomédico se creó correctamente.',
          icon: 'pi pi-check',
          typeOfNotification: ETypeOfNotification.INFO,
        }))
      }

      if (formState.active) {
        await deactivateOtherConfigs()
      }

      await loadData()
      resetForm()
      return true
    } catch (error: unknown) {
      const response = (error as any)?.response
      const status = typeof response?.status === 'number' ? response.status : 0
      if (status === 409) {
        dispatch(setNotification({
          title: 'No se pudo guardar',
          desc: 'Ya existe una configuración de equipo biomédico con esos datos.',
          icon: 'pi pi-exclamation-triangle',
          typeOfNotification: ETypeOfNotification.WARNING,
        }))
      } else {
        dispatch(setNotification({
          title: 'Error',
          desc: 'No se pudo guardar la configuración de equipo biomédico.',
          icon: 'pi pi-times',
          typeOfNotification: ETypeOfNotification.ERROR,
        }))
      }
      return false
    }
  }

  function requestSave(context: 'inline' | 'mobile' = 'inline') {
    const validationErrors = validateForm()
    setFormErrors(validationErrors)
    if (Object.keys(validationErrors).length > 0) return

    if (shouldConfirmActivation()) {
      setPendingSaveContext(context)
      setActiveConfirmVisible(true)
      return
    }

    if (context === 'mobile') {
      void saveFromMobileModal()
    } else {
      void submitSave()
    }
  }

  async function confirmActivation() {
    const context = pendingSaveContext
    setActiveConfirmVisible(false)
    setPendingSaveContext(null)

    const result = await submitSave()
    if (result && context === 'mobile') {
      setMobileModalVisible(false)
    }
  }

  function cancelActivation() {
    setActiveConfirmVisible(false)
    setPendingSaveContext(null)
  }

  function startEdit(
    item: BiomedicalEquipmentConfigItemDTO,
    index: number,
    options?: { scrollAndFocus?: boolean },
  ): boolean {
    const shouldScrollAndFocus = options?.scrollAndFocus ?? true
    if (shouldScrollAndFocus) {
      inputTableTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    setEditIndex(index)
    setFormErrors({})
    setFormState({
      biomedicalEquipmentConfig: item.biomedicalEquipmentConfig,
      name: item.name ?? '',
      aeTitle: item.aeTitle ?? '',
      url: item.url ?? '',
      port: item.port ? String(item.port) : '',
      providerSiteCode: item.providerSiteCode ?? '',
      user: item.user ?? '',
      password: item.password ?? '',
      active: item.active,
      connectionType: item.connectionType ?? BiomedicalEquipmentConnectionType.INTERNAL,
    })
    if (shouldScrollAndFocus) {
      setFocusNameInputToken((current) => current + 1)
    }
    return true
  }

  function formatCardValue(value: string | null | undefined, emptyText = 'Sin datos') {
    const normalized = String(value ?? '').trim()
    return normalized !== '' ? normalized : emptyText
  }

  function openAddModal() {
    resetForm()
    setMobileModalVisible(true)
  }

  function openEditModal(item: BiomedicalEquipmentConfigItemDTO, index: number) {
    startEdit(item, index, { scrollAndFocus: false })
    setMobileModalVisible(true)
  }

  function closeMobileModal() {
    setMobileModalVisible(false)
    resetForm()
  }

  async function saveFromMobileModal() {
    const result = await submitSave()
    if (result) {
      setMobileModalVisible(false)
    }
  }

  function confirmDelete(item: BiomedicalEquipmentConfigItemDTO) {
    setItemToDelete(item)
    setDeleteModalVisible(true)
  }

  async function handleDelete() {
    if (!itemToDelete) {
      setDeleteModalVisible(false)
      return
    }

    try {
      await deleteBiomedicalEquipmentConfig(itemToDelete.biomedicalEquipmentConfig)
      dispatch(setNotification({
        title: 'Eliminación exitosa',
        desc: 'La configuración de equipo biomédico se eliminó correctamente.',
        icon: 'pi pi-check',
        typeOfNotification: ETypeOfNotification.INFO,
      }))
      await loadData()
    } catch (error: unknown) {
      const response = (error as any)?.response
      const status = typeof response?.status === 'number' ? response.status : 0
      if (status === 409) {
        dispatch(setNotification({
          title: 'No se puede eliminar',
          desc: 'La configuración de equipo biomédico está siendo utilizada en otras relaciones.',
          icon: 'pi pi-exclamation-triangle',
          typeOfNotification: ETypeOfNotification.WARNING,
        }))
      } else {
        dispatch(setNotification({
          title: 'Error',
          desc: 'No se pudo eliminar la configuración de equipo biomédico.',
          icon: 'pi pi-times',
          typeOfNotification: ETypeOfNotification.ERROR,
        }))
      }
    } finally {
      setDeleteModalVisible(false)
      setItemToDelete(null)
    }
  }

  const columns: InputColumnTableProps<BiomedicalEquipmentConfigItemDTO>[] = [
    {
      header: 'Nombre',
      className: 'medical-specialities-col medical-specialities-col--name',
      style: { paddingLeft: '8px' },
      body: (item) => item.name,
      input: (
        <InputTextCustom
          className={`biomedical-equipment-config-name-input w-full ${formErrors.name ? '' : 'border-grey'}`}
          value={formState.name}
          onChange={(event) => {
            setFormState((current) => ({ ...current, name: event.target.value }))
            setFormErrors((current) => ({ ...current, name: undefined }))
          }}
          error={Boolean(formErrors.name)}
          caption={formErrors.name}
        />
      ),
    },
    {
      header: 'AE Title',
      className: 'medical-specialities-col medical-specialities-col--profile',
      body: (item) => item.aeTitle,
      input: (
        <InputTextCustom
          className={`w-full ${formErrors.aeTitle ? '' : 'border-grey'}`}
          value={formState.aeTitle}
          onChange={(event) => {
            setFormState((current) => ({ ...current, aeTitle: event.target.value }))
            setFormErrors((current) => ({ ...current, aeTitle: undefined }))
          }}
          error={Boolean(formErrors.aeTitle)}
          caption={formErrors.aeTitle}
        />
      ),
    },
    {
      header: 'URL',
      className: 'medical-specialities-col medical-specialities-col--url',
      body: (item) => item.url,
      input: (
        <InputTextCustom
          className={`w-full ${formErrors.url ? '' : 'border-grey'}`}
          value={formState.url}
          onChange={(event) => {
            setFormState((current) => ({ ...current, url: event.target.value }))
            setFormErrors((current) => ({ ...current, url: undefined }))
          }}
          error={Boolean(formErrors.url)}
          caption={formErrors.url}
        />
      ),
    },
    {
      header: 'Tipo de conexión',
      className: 'medical-specialities-col medical-specialities-col--profile',
      body: (item) => connectionTypeLabel(item.connectionType),
      input: (
        <Dropdown
          className='w-full'
          value={formState.connectionType}
          options={connectionTypeOptions}
          optionLabel='label'
          optionValue='value'
          onChange={(event) => handleConnectionTypeChange(event.value)}
        />
      ),
    },
    {
      header: 'Puerto / Código de sitio',
      className: 'medical-specialities-col medical-specialities-col--port',
      body: (item) =>
        item.connectionType === BiomedicalEquipmentConnectionType.EXTERNAL
          ? item.providerSiteCode
          : item.port,
      input:
        formState.connectionType === BiomedicalEquipmentConnectionType.EXTERNAL ? (
          <InputTextCustom
            className={`w-full ${formErrors.providerSiteCode ? '' : 'border-grey'}`}
            value={formState.providerSiteCode}
            onChange={(event) => {
              setFormState((current) => ({ ...current, providerSiteCode: event.target.value }))
              setFormErrors((current) => ({ ...current, providerSiteCode: undefined }))
            }}
            error={Boolean(formErrors.providerSiteCode)}
            caption={formErrors.providerSiteCode}
          />
        ) : (
          <InputTextCustom
            className={`w-full ${formErrors.port ? '' : 'border-grey'}`}
            value={formState.port}
            onChange={(event) => {
              setFormState((current) => ({ ...current, port: event.target.value }))
              setFormErrors((current) => ({ ...current, port: undefined }))
            }}
            error={Boolean(formErrors.port)}
            caption={formErrors.port}
          />
        ),
    },
    {
      header: 'Usuario',
      className: 'medical-specialities-col medical-specialities-col--profile',
      body: (item) => item.user,
      input: (
        <InputTextCustom
          className={`w-full ${formErrors.user ? '' : 'border-grey'}`}
          value={formState.user}
          onChange={(event) => {
            setFormState((current) => ({ ...current, user: event.target.value }))
            setFormErrors((current) => ({ ...current, user: undefined }))
          }}
          error={Boolean(formErrors.user)}
          caption={formErrors.user}
        />
      ),
    },
    {
      header: 'Contraseña',
      className: 'medical-specialities-col medical-specialities-col--profile',
      body: () => '••••••••',
      input: (
        <InputTextCustom
          className={`w-full ${formErrors.password ? '' : 'border-grey'}`}
          value={formState.password}
          inputType={EInputTypes.PASSWORD}
          onChange={(event) => {
            setFormState((current) => ({ ...current, password: event.target.value }))
            setFormErrors((current) => ({ ...current, password: undefined }))
          }}
          error={Boolean(formErrors.password)}
          caption={formErrors.password}
        />
      ),
    },
    {
      header: 'Activo',
      className: 'medical-specialities-col medical-specialities-col--allows',
      body: (item) => (
        <span className='medical-specialities-allows-value'>
          {item.active ? 'Sí' : 'No'}
        </span>
      ),
      input: (
        <div className='medical-specialities-switch location-switch-inline-row location-desktop-inputtable-switch-row pb-2'>
          <InputSwitch
            id='biomedicalEquipmentConfigActiveInputSwitchId'
            className='w-full'
            checked={formState.active}
            onChange={(event) =>
              setFormState((current) => ({ ...current, active: Boolean(event.value) }))
            }
          />
          <span className='medical-specialities-switch-state'>{formState.active ? 'Sí' : 'No'}</span>
        </div>
      ),
    },
    {
      header: '',
      className: 'medical-specialities-col medical-specialities-col--actions',
      input: isEditing ? (
        <LocationInlineEditActions
          visible
          onConfirm={() => {
            requestSave('inline')
          }}
          onCancel={resetForm}
        />
      ) : (
        <Button
          type='button'
          icon='pi pi-plus-circle text-3xl'
          className='buttonSecondary2 p-button-rounded medical-specialities-add-btn'
          onClick={() => {
            requestSave('inline')
          }}
          aria-label='Crear configuración de equipo biomédico'
        />
      ),
      body: (item, index) => (
        <div className='medical-specialities-actions-body'>
          <Button
            type='button'
            icon='pi pi-file-edit'
            className='p-button-text medical-specialities-action-icon-btn'
            onClick={() => startEdit(item, index)}
            aria-label='Editar configuración de equipo biomédico'
          />
          <Button
            type='button'
            icon='pi pi-trash'
            className='p-button-text medical-specialities-action-icon-btn'
            onClick={() => confirmDelete(item)}
            aria-label='Eliminar configuración de equipo biomédico'
          />
        </div>
      ),
    },
  ]

  if (isMobile) {
    return (
      <div className='viewContainer medical-specialities-view medical-specialities-mobile-view'>
        <div className='medical-specialities-header'>
          <h1 className='medical-specialities-title'>Configuración equipo biomédico</h1>
          <div className='medical-specialities-subtitle'>
            <i className='pi pi-info-circle medical-specialities-subtitle-icon' />
            <span>Solo se podrá tener uno activo.</span>
          </div>
          <div className='medical-specialities-subtitle'>
            <i className='pi pi-info-circle medical-specialities-subtitle-icon' />
            <span>Interno: para conectarse a un PACS en una red privada. Externo: para conectarse a una API.</span>
          </div>
        </div>
        <div className='medical-specialities-divider' />

        <div className='medical-specialities-mobile-toolbar'>
          <Button
            className='medical-specialities-mobile-add-btn'
            icon='pi pi-plus-circle'
            label='Agregar'
            onClick={openAddModal}
          />
        </div>

        <div className='medical-specialities-mobile-card-list'>
          {(items ?? []).map((item, index) => {
            const menuItems: LocationMobileActionItem[] = [
              {
                label: 'Editar',
                icon: 'pi pi-file-edit',
                action: () => openEditModal(item, index),
              },
              {
                label: 'Eliminar',
                icon: 'pi pi-trash',
                action: () => confirmDelete(item),
              },
            ]

            return (
              <article key={`biomedical-equipment-config-${item.biomedicalEquipmentConfig}-${index}`} className='medical-specialities-mobile-card'>
                <div className='medical-specialities-mobile-card-header'>
                  <h4 className='medical-specialities-mobile-card-title'>{formatCardValue(item.name, 'Sin nombre')}</h4>
                  <LocationMobileActionMenu items={menuItems} ariaLabel='Opciones de la configuración de equipo biomédico' />
                </div>
                <div className='medical-specialities-mobile-card-row'>
                  <span className='medical-specialities-mobile-card-key'>AE Title</span>
                  <span className='medical-specialities-mobile-card-value'>{formatCardValue(item.aeTitle)}</span>
                </div>
                <div className='medical-specialities-mobile-card-row'>
                  <span className='medical-specialities-mobile-card-key'>URL</span>
                  <span className='medical-specialities-mobile-card-value'>{formatCardValue(item.url)}</span>
                </div>
                <div className='medical-specialities-mobile-card-row'>
                  <span className='medical-specialities-mobile-card-key'>Tipo de conexión</span>
                  <span className='medical-specialities-mobile-card-value'>{connectionTypeLabel(item.connectionType)}</span>
                </div>
                <div className='medical-specialities-mobile-card-row'>
                  <span className='medical-specialities-mobile-card-key'>
                    {item.connectionType === BiomedicalEquipmentConnectionType.EXTERNAL ? 'Código de sitio' : 'Puerto'}
                  </span>
                  <span className='medical-specialities-mobile-card-value'>
                    {item.connectionType === BiomedicalEquipmentConnectionType.EXTERNAL
                      ? formatCardValue(item.providerSiteCode)
                      : formatCardValue(item.port ? String(item.port) : '')}
                  </span>
                </div>
                <div className='medical-specialities-mobile-card-row'>
                  <span className='medical-specialities-mobile-card-key'>Usuario</span>
                  <span className='medical-specialities-mobile-card-value'>{formatCardValue(item.user)}</span>
                </div>
                <div className='medical-specialities-mobile-card-row'>
                  <span className='medical-specialities-mobile-card-key'>Activo</span>
                  <span className='medical-specialities-mobile-card-value'>{item.active ? 'Sí' : 'No'}</span>
                </div>
              </article>
            )
          })}
          {(items ?? []).length === 0 && (
            <p className='medical-specialities-mobile-empty'>No hay configuraciones de equipos biomédicos cargadas.</p>
          )}
        </div>

        <Modal
          className='medical-specialities-mobile-editor-modal'
          visible={mobileModalVisible}
          setVisible={setMobileModalVisible}
          width='w-medium'
          header={<h1 className='info-dark text-2xl'>{isEditing ? 'Editar configuración de equipo biomédico' : 'Agregar configuración de equipo biomédico'}</h1>}
          footerButtonLeftText='Cancelar'
          onClickLeftBtn={closeMobileModal}
          footerButtonRightText={isEditing ? 'Guardar' : 'Agregar'}
          onClickRightBtn={() => { requestSave('mobile') }}
          onHideCustom={closeMobileModal}
        >
          <div className='medical-specialities-mobile-modal-content'>
            <div>
              <label className='block mb-2'>Nombre <span className='text-red-500'>*</span></label>
              <InputTextCustom
                className={`biomedical-equipment-config-name-input w-full ${formErrors.name ? '' : 'border-grey'}`}
                value={formState.name}
                onChange={(event) => {
                  setFormState((current) => ({ ...current, name: event.target.value }))
                  setFormErrors((current) => ({ ...current, name: undefined }))
                }}
                error={Boolean(formErrors.name)}
                caption={formErrors.name}
              />
            </div>
            <div>
              <label className='block mb-2'>AE Title</label>
              <InputTextCustom
                className={`w-full ${formErrors.aeTitle ? '' : 'border-grey'}`}
                value={formState.aeTitle}
                onChange={(event) => {
                  setFormState((current) => ({ ...current, aeTitle: event.target.value }))
                  setFormErrors((current) => ({ ...current, aeTitle: undefined }))
                }}
                error={Boolean(formErrors.aeTitle)}
                caption={formErrors.aeTitle}
              />
            </div>
            <div>
              <label className='block mb-2'>URL</label>
              <InputTextCustom
                className={`w-full ${formErrors.url ? '' : 'border-grey'}`}
                value={formState.url}
                onChange={(event) => {
                  setFormState((current) => ({ ...current, url: event.target.value }))
                  setFormErrors((current) => ({ ...current, url: undefined }))
                }}
                error={Boolean(formErrors.url)}
                caption={formErrors.url}
              />
            </div>
            <div>
              <label className='block mb-2'>Tipo de conexión</label>
              <Dropdown
                className='w-full'
                value={formState.connectionType}
                options={connectionTypeOptions}
                optionLabel='label'
                optionValue='value'
                onChange={(event) => handleConnectionTypeChange(event.value)}
              />
            </div>
            {formState.connectionType === BiomedicalEquipmentConnectionType.EXTERNAL ? (
              <div>
                <label className='block mb-2'>Código de sitio del proveedor <span className='text-red-500'>*</span></label>
                <InputTextCustom
                  className={`w-full ${formErrors.providerSiteCode ? '' : 'border-grey'}`}
                  value={formState.providerSiteCode}
                  onChange={(event) => {
                    setFormState((current) => ({ ...current, providerSiteCode: event.target.value }))
                    setFormErrors((current) => ({ ...current, providerSiteCode: undefined }))
                  }}
                  error={Boolean(formErrors.providerSiteCode)}
                  caption={formErrors.providerSiteCode}
                />
              </div>
            ) : (
              <div>
                <label className='block mb-2'>Puerto</label>
                <InputTextCustom
                  className={`w-full ${formErrors.port ? '' : 'border-grey'}`}
                  value={formState.port}
                  onChange={(event) => {
                    setFormState((current) => ({ ...current, port: event.target.value }))
                    setFormErrors((current) => ({ ...current, port: undefined }))
                  }}
                  error={Boolean(formErrors.port)}
                  caption={formErrors.port}
                />
              </div>
            )}
            <div>
              <label className='block mb-2'>Usuario</label>
              <InputTextCustom
                className={`w-full ${formErrors.user ? '' : 'border-grey'}`}
                value={formState.user}
                onChange={(event) => {
                  setFormState((current) => ({ ...current, user: event.target.value }))
                  setFormErrors((current) => ({ ...current, user: undefined }))
                }}
                error={Boolean(formErrors.user)}
                caption={formErrors.user}
              />
            </div>
            <div>
              <label className='block mb-2'>Contraseña</label>
              <InputTextCustom
                className={`w-full ${formErrors.password ? '' : 'border-grey'}`}
                value={formState.password}
                inputType={EInputTypes.PASSWORD}
                onChange={(event) => {
                  setFormState((current) => ({ ...current, password: event.target.value }))
                  setFormErrors((current) => ({ ...current, password: undefined }))
                }}
                error={Boolean(formErrors.password)}
                caption={formErrors.password}
              />
            </div>
            <div>
              <label className='block mb-2'>Activo</label>
            </div>
            <div className='medical-specialities-mobile-switch-row location-switch-inline-row' style={{display: 'inline-flex', alignItems: 'center', gap: '8px'}}>
              <InputSwitch
                id='biomedicalEquipmentConfigActiveMobileInputSwitchId'
                checked={formState.active}
                onChange={(event) =>
                  setFormState((current) => ({ ...current, active: Boolean(event.value) }))
                }
              />
              <span className='medical-specialities-switch-state'>{formState.active ? 'Sí' : 'No'}</span>
            </div>
          </div>
        </Modal>

        <Modal
          visible={deleteModalVisible}
          setVisible={setDeleteModalVisible}
          width='w-medium'
          header={<p className='info-dark text-2xl'>Eliminar configuración de equipo biomédico</p>}
          footerButtonLeftText='Cancelar'
          footerButtonRightText='Sí, eliminar'
          onClickLeftBtn={() => {
            setDeleteModalVisible(false)
            setItemToDelete(null)
          }}
          onClickRightBtn={() => {
            void handleDelete()
          }}
          onHideCustom={() => {
            setDeleteModalVisible(false)
            setItemToDelete(null)
          }}
        >
          <p className='medical-specialities-delete-message'>
            ¿Querés eliminar la configuración de equipo biomédico{' '}
            <strong>{itemToDelete?.name ?? ''}</strong>?
          </p>
        </Modal>

        <Modal
          visible={activeConfirmVisible}
          setVisible={setActiveConfirmVisible}
          width='w-medium'
          header={<p className='info-dark text-2xl'>Activar configuración de equipo biomédico</p>}
          footerButtonLeftText='Cancelar'
          footerButtonRightText='Sí, activar'
          onClickLeftBtn={cancelActivation}
          onClickRightBtn={() => {
            void confirmActivation()
          }}
          onHideCustom={cancelActivation}
        >
          <p className='medical-specialities-delete-message'>
            Solo puede haber una configuración activa. Al activar esta configuración, las demás configuraciones activas se desactivarán automáticamente. ¿Querés continuar?
          </p>
        </Modal>
      </div>
    )
  }

  return (
    <div className='viewContainer medical-specialities-view'>
      <div className='medical-specialities-header'>
        <h1 className='medical-specialities-title'>Configuración equipo biomédico</h1>
        <h2 className="title3 mb-2 mt-3">PACS</h2>
        <div className='medical-specialities-subtitle'>
          <i className='pi pi-info-circle medical-specialities-subtitle-icon' />
          <span>Solo se podrá tener uno activo.</span>
        </div>
        <div className='medical-specialities-subtitle'>
          <i className='pi pi-info-circle medical-specialities-subtitle-icon' />
          <span>Interno: para conectarse a un PACS en una red privada. Externo: para conectarse a una API.</span>
        </div>
      </div>
      <div className='medical-specialities-divider' />

      {!loading && (
        <div ref={inputTableTopRef}>
          <InputTable
            className='medical-specialities-inputtable'
            columnGap={0}
            headerDivClassName='font-semibold bg-bluegray-50'
            dividerClassName='my-1 border-divider'
            items={items}
            columns={columns}
            onAdd={() => {}}
            divBtnClassName='medical-specialities-hide-native-control'
            divider
            dividerInFirstItem
          />
        </div>
      )}

      <Modal
        visible={deleteModalVisible}
        setVisible={setDeleteModalVisible}
        width='w-medium'
        header={<p className='info-dark text-2xl'>Eliminar configuración de equipo biomédico</p>}
        footerButtonLeftText='Cancelar'
        footerButtonRightText='Sí, eliminar'
        onClickLeftBtn={() => {
          setDeleteModalVisible(false)
          setItemToDelete(null)
        }}
        onClickRightBtn={() => {
          void handleDelete()
        }}
        onHideCustom={() => {
          setDeleteModalVisible(false)
          setItemToDelete(null)
        }}
      >
        <p className='medical-specialities-delete-message'>
          ¿Querés eliminar la configuración de equipo biomédico{' '}
          <strong>{itemToDelete?.name ?? ''}</strong>?
        </p>
      </Modal>

      <Modal
        visible={activeConfirmVisible}
        setVisible={setActiveConfirmVisible}
        width='w-medium'
        header={<p className='info-dark text-2xl'>Activar configuración de equipo biomédico</p>}
        footerButtonLeftText='Cancelar'
        footerButtonRightText='Sí, activar'
        onClickLeftBtn={cancelActivation}
        onClickRightBtn={() => {
          void confirmActivation()
        }}
        onHideCustom={cancelActivation}
      >
        <p className='medical-specialities-delete-message'>
          Solo puede haber una configuración activa. Al activar esta configuración, las demás configuraciones activas se desactivarán automáticamente. ¿Querés continuar?
        </p>
      </Modal>
    </div>
  )
}
